import React from "react";
import { SEO } from "@/components/SEO";
import { Reveal, RevealText } from "@/components/brand/Reveal";
import { BrandButton } from "@/components/brand/BrandButton";
import { BrandCard } from "@/components/brand/BrandCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const AccordionAny: any = Accordion;
const AccordionItemAny: any = AccordionItem;
const AccordionTriggerAny: any = AccordionTrigger;
const AccordionContentAny: any = AccordionContent;
import { SUPPORT_EMAIL, PUBLISHER, SITE_NAME, SITE_URL } from "@/content/siteConfig";

const faqs = [
  {
    question: "Does CrenelView send any data anywhere?",
    answer: "No. CrenelView has no INTERNET permission, no account, and no server. Nothing about your phone or your child leaves the device.",
  },
  {
    question: "Does blocking work per app, or for my whole list?",
    answer: "It is one setting for your whole list. You choose whether every app you have ticked is blocked all the time, or only during hours you set. It is not a per app schedule.",
  },
  {
    question: "Do I need to buy my child a separate phone?",
    answer: "No. CrenelView is built to run on the one phone you already share with your child, not on a second device.",
  },
  {
    question: "What is the one manual step in setup?",
    answer: "Turning on CrenelView's accessibility service in your phone's Settings app. It is required for blocking to work, and it is the only step you do by hand, outside the app.",
  },
  {
    question: "Does 'Allow for a while' unlock just one app?",
    answer: "No. It opens your entire blocked list for the window you choose, 10, 15, 30 or 60 minutes, then blocks everything again automatically.",
  },
];

export default function Support() {
  return (
    <>
      <SEO
        title={`Support - ${SITE_NAME}`}
        description="Get in touch with the CrenelView team by email for any questions, bugs or feedback."
        path="/support"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: `Support - ${SITE_NAME}`,
            url: `${SITE_URL}/support`,
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          },
        ]}
      />

      <section className="mx-auto max-w-3xl px-6 pt-14 pb-10 sm:pt-20">
        <RevealText lines={["Support."]} className="font-heading text-4xl sm:text-5xl lg:text-6xl" as="h1" />
        <Reveal delay={0.3}>
          <p className="mt-6 max-w-lg text-base text-cvbody sm:text-lg">
            Questions, bugs, or anything not working the way it should - email us directly. We read every message ourselves.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-16">
        <Reveal>
          <BrandCard className="flex flex-col items-start gap-6 p-8 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-heading text-xl">{SUPPORT_EMAIL}</p>
              <p className="mt-2 text-sm text-cvmuted">Published by {PUBLISHER}</p>
            </div>
            <BrandButton href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email-cta">
              Email support
            </BrandButton>
          </BrandCard>
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-20 sm:pb-28">
        <Reveal>
          <h2 className="font-heading text-2xl sm:text-3xl">Common questions.</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="support-faq">
            {faqs.map((faq, i) => (
              <AccordionItemAny key={faq.question} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTriggerAny data-testid={`faq-question-${i}`}>{faq.question}</AccordionTriggerAny>
                <AccordionContentAny className="text-base text-cvbody" data-testid={`faq-answer-${i}`}>
                  {faq.answer}
                </AccordionContentAny>
              </AccordionItemAny>
            ))}
          </Accordion>
        </Reveal>
      </section>
    </>
  );
}
